import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import { ApiError, api, type AppSettings, type Folder, type ForwardRule, type LastSync, type Me, type MessageRow, type SearchParams, type SendResult } from "./api";
import { SignedOut, signOut, startSignIn } from "./auth";
import { SELECT_ALL_MAX, bulkActions, busyText, movedText, readText, resultText, starredText, trashedText } from "./bulk";
import { COMPOSE_TITLES, Compose } from "./Compose";
import { Drafts } from "./Drafts";
import { FolderList } from "./FolderList";
import { Icon } from "./icons";
import { MessageList } from "./MessageList";
import { MessageView } from "./MessageView";
import { NotificationSettings } from "./NotificationSettings";
import { NotKept, forgetOfflineCopy, loadSettings, refreshStatus, remembered, syncOffline } from "./offline";
import { OfflineSettings } from "./OfflineSettings";
import { Outbox } from "./Outbox";
import { type BulkAction, type Performed, type SingleAction, actionFolder, flushOutbox, forgetOutbox, markFolderRead, onOutbox, perform, refreshOutbox, resumeTurns, useOutbox } from "./outbox";
import { draftFor, draftTarget, newKey } from "./outgoing";
import { type Change, type Overrides, begin, settle, tick } from "./overrides";
import { isNetworkError, isOnline, useOnline } from "./net";
import { takeTap } from "./notify";
import { closeNotification, followTaps, keepRegistered, pushFolders } from "./push";
import { type ComposeMode, type Route, back, navigate, parseRoute, useRoute } from "./route";
import { RuleList, RulePane } from "./Rules";
import { SearchForm } from "./SearchForm";
import { BulkBar, SelectBar } from "./Selection";

const POLL_MS = 30_000;
/** While the server cannot be reached (and the device says it is online): how often to try again. The browser's
 * "online" event is not to be relied on (iOS especially), so this is how the app notices the server is back. */
const RETRY_MS = 5_000;
const NOTICE_MS = 3_000;
/** How long a message swiped to Trash can be taken back. */
const UNDO_MS = 5_000;
/** How long a notice with something to do (Retry) stays. */
const ACTION_NOTICE_MS = 15_000;

/** An action on a message for actAll, with the change shown in the lists until it is done (null: shown already). */
type Acted = { action: SingleAction; subject: string | null; change: Change | null };

/** Keeping current (docs/WEBMAIL.md): on opening, on returning to the foreground and on pull-to-refresh the app asks
 * the server to check Inbox and the folder on screen against Yahoo (POST /api/sync); while visible it polls
 * /api/folders every 30 s and reloads the listing only when the folder's change marker moved. Every answer brings
 * the offline copy up to date (offline.ts); without a connection the app shows the copy, starting from the folders,
 * settings and account it kept, and checks again as soon as the device is back online. Mail written and actions taken
 * without a connection wait in the outbox (outbox.ts), sent when the app starts or comes back online and after every
 * /api/folders answer. New-mail notifications (push.ts, sw.ts) open the message they are about; the icon's badge
 * counts the unread mail in the folders notified. Actions (read, star, move, trash, a folder marked read) show at once
 * and are done in the background, one after another (overrides.ts, outbox.inTurn); those the app was closed before it
 * came to are done when it starts, comes to the front or comes back online (outbox.resumeTurns). Those waiting in the
 * outbox, or left by a closed page, show over the server's listings until they are done (outbox.waiting). The starred
 * mail of every folder has a view of its own, reloaded when any folder changes. A message swiped to Trash in the list
 * leaves it at once and is sent once its notice's Undo has had its time (UNDO_MS), another notice took its place or the
 * app went to the background. Several messages chosen in the list (the toolbar's Select) are deleted the same way, and
 * moved, starred or marked read with one action each, as a single message is, and one notice for them all. */
export function App({ open, signedIn: initiallySignedIn, signInError }: { open: boolean; signedIn: boolean; signInError: string | null }) {
  const route = useRoute();
  const [signedIn, setSignedIn] = useState(initiallySignedIn);
  const [authError, setAuthError] = useState(signInError);
  const [me, setMe] = useState<Me | null>(null);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [lastSync, setLastSync] = useState<LastSync | null>(null);
  const [version, setVersion] = useState(0);
  const [overrides, setOverrides] = useState<Overrides>(new Map());
  const [folderReads, setFolderReads] = useState<Overrides>(new Map());
  const [drawer, setDrawer] = useState(false);
  /** The messages ticked in the list while several are being chosen (null: not choosing), and the rows the list shows
   * meanwhile, as it shows them: what select all ticks and the actions are done on. */
  const [checked, setChecked] = useState<Set<string> | null>(null);
  const [listed, setListed] = useState<MessageRow[]>([]);
  /** How many messages the listing has in all (null: not known) and whether every one is loaded; and whether Select
   * all is loading the rest of them, ticking the rows as they come. */
  const [listing, setListing] = useState<{ total: number | null; complete: boolean }>({ total: null, complete: false });
  const [selectingAll, setSelectingAll] = useState(false);
  /** `undo`: the notice of a message swiped to Trash, with Undo (undoable). `sticky`: stays until the next notice (an
   * action on several messages being done). `action`: something to do about it (Retry the messages not done). */
  const [notice, setNotice] = useState<{ text: string; error: boolean; undo?: boolean; sticky?: boolean; action?: { label: string; run: () => void } } | null>(null);
  const [rules, setRules] = useState<ForwardRule[] | null>(null);
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const online = useOnline();
  const outbox = useOutbox();
  const changes = useRef(new Map<string, number>());
  const lastPoll = useRef(0);
  const routeRef = useRef(route);
  routeRef.current = route;
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  /** The message swiped to Trash whose notice is up: sent (commit) or taken back (undo) when the notice goes. */
  const undoable = useRef<{ commit: () => void; undo: () => void } | null>(null);
  const endUndo = useCallback((commit: boolean) => {
    const u = undoable.current;
    undoable.current = null;
    if (u) (commit ? u.commit : u.undo)();
  }, []);

  const currentFolder = route.view === "folder" ? route.folder : null;
  const inRules = route.view === "rules";
  const inSettings = route.view === "settings";
  const inNotifications = route.view === "notifications";
  const inOutbox = route.view === "outbox";
  const inDrafts = route.view === "drafts";
  const inStarred = route.view === "starred";
  const inPage = inRules || inSettings || inNotifications || inOutbox || inDrafts;

  const showNotice = useCallback((text: string, error = false) => {
    endUndo(true);
    setNotice({ text, error });
  }, []);
  /** A notice that stays until the next one: an action on several messages being done. */
  const showBusy = useCallback((text: string) => {
    endUndo(true);
    setNotice({ text, error: false, sticky: true });
  }, []);
  /** What an action on several messages did, with Retry for the messages it did not (`retry`). */
  const showResult = useCallback((text: string, error: boolean, retry: (() => void) | null) => {
    endUndo(true);
    setNotice({ text, error, action: retry ? { label: "Retry", run: retry } : undefined });
  }, []);
  /** The notice goes, unless another took its place meanwhile (the delete an Undo notice commits shows its own). */
  const dropNotice = useCallback((shown: NonNullable<typeof notice>) => setNotice((n) => (n === shown ? null : n)), []);
  useEffect(() => {
    if (!notice || notice.sticky) return;
    const t = setTimeout(
      () => {
        if (notice.undo) endUndo(true);
        dropNotice(notice);
      },
      notice.undo ? UNDO_MS : notice.action ? ACTION_NOTICE_MS : notice.error ? NOTICE_MS * 2 : NOTICE_MS,
    );
    return () => clearTimeout(t);
  }, [notice]);

  const errorText = (e: unknown) => (isNetworkError(e) ? "No connection to the server" : e instanceof ApiError || e instanceof NotKept ? e.message : String(e));
  const onError = useCallback((e: unknown) => {
    if (e instanceof SignedOut) {
      setSignedIn(false);
      setAuthError(e.message === "Signed out" ? null : e.message);
      return;
    }
    console.error(e);
    showNotice(errorText(e), true);
  }, []);
  /** Keeping current runs by itself: without a connection it fails quietly (the offline banner says so). */
  const onBackgroundError = useCallback((e: unknown) => {
    if (!isNetworkError(e)) onError(e);
  }, []);

  const reloadList = () => setVersion((v) => v + 1);

  /** Folder counts and change markers; reloads the listing on screen when its folder changed (the starred mail: when
   * any folder did). */
  const loadFolders = useCallback(async () => {
    lastPoll.current = Date.now();
    try {
      const res = await api.folders();
      setFolders(res.folders);
      setLastSync(res.last_sync);
      remembered.keepFolders(res);
      void flushOutbox(); // the server answers: what waits in the outbox goes now
      const r = routeRef.current;
      const shown = r.view === "folder" ? r.folder : null;
      let moved = false;
      for (const f of res.folders) {
        const before = changes.current.get(f.name);
        if (before !== undefined && before !== f.change && (f.name === shown || r.view === "starred")) moved = true;
        changes.current.set(f.name, f.change);
      }
      if (moved) reloadList();
      if ("setAppBadge" in navigator) {
        const notified = new Set(pushFolders(settingsRef.current).map((f) => f.toLowerCase()));
        const unread = res.folders.reduce((n, f) => n + (notified.has(f.name.toLowerCase()) ? f.unread : 0), 0);
        (unread ? navigator.setAppBadge(unread) : navigator.clearAppBadge()).catch(() => {});
      }
    } catch (e) {
      onBackgroundError(e);
    }
  }, []);

  /** "Check now": Inbox and the folder on screen against Yahoo, then the markers. */
  const checkNow = useCallback(async () => {
    const r = routeRef.current;
    const names = Array.from(new Set(["Inbox", ...(r.view === "folder" ? [r.folder] : [])]));
    try {
      const results = await Promise.all(names.map((n) => api.sync(n)));
      await loadFolders();
      if (results.some((x) => x.busy || x.truncated)) setTimeout(loadFolders, 5_000);
    } catch (e) {
      onBackgroundError(e);
    }
  }, []);

  const loadAppSettings = useCallback(() => {
    loadSettings().then((s) => s && setSettings(s), onBackgroundError);
  }, []);

  useEffect(() => {
    if (!signedIn) return;
    // What the app kept from last time, at once (it is all there is without a connection); the server's answers
    // replace it as they come.
    refreshStatus();
    refreshOutbox();
    void resumeTurns();
    remembered.folders().then((kept) => {
      if (!kept) return;
      setFolders((now) => (now.length ? now : kept.folders));
      setLastSync((now) => now ?? kept.last_sync);
    });
    remembered.me().then((kept) => kept && setMe((now) => now ?? kept));
    remembered.settings().then((kept) => kept && setSettings((now) => now ?? kept));
    api.me().then((m) => {
      setMe(m);
      remembered.keepMe(m);
    }, onBackgroundError);
    loadAppSettings();
    loadFolders();
    checkNow();
    keepRegistered().catch(onBackgroundError);
    const onVisible = () => {
      if (document.visibilityState !== "visible") {
        // the app may not be back: a message swiped to Trash goes now
        endUndo(true);
        setNotice((n) => (n?.undo ? null : n));
        return;
      }
      void resumeTurns(); // another tab's, closed meanwhile
      checkNow();
      loadAppSettings();
    };
    document.addEventListener("visibilitychange", onVisible);
    const timer = setInterval(() => {
      if (document.visibilityState !== "visible" || navigator.onLine === false) return;
      if (Date.now() - lastPoll.current >= (isOnline() ? POLL_MS : RETRY_MS) - 500) loadFolders();
    }, RETRY_MS);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(timer);
    };
  }, [signedIn]);

  // The service worker: a push came (the folders now, not at the next poll), or a notification was tapped (go to it:
  // told by a message, or kept for the app to find when it starts or comes to the front, as the iPhone needs).
  useEffect(() => {
    if (!signedIn) return;
    const open = (to: string) => {
      const url = new URL(to, location.origin);
      if (url.origin !== location.origin) return;
      setDrawer(false);
      navigate(parseRoute(url));
    };
    const stopTaps = followTaps(open);
    if (!("serviceWorker" in navigator)) return stopTaps;
    const onMessage = (e: MessageEvent) => {
      const data = e.data as { type?: string; url?: string } | null;
      if (data?.type === "push") loadFolders();
      else if (data?.type === "open" && data.url) {
        takeTap().catch(() => {}); // followed now, not again when the app next comes to the front
        open(data.url);
      }
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    navigator.serviceWorker.startMessages();
    return () => {
      stopTaps();
      navigator.serviceWorker.removeEventListener("message", onMessage);
    };
  }, [signedIn]);

  // The outbox: what it sent or could not send is told; the folders follow what left it.
  useEffect(
    () =>
      onOutbox((e) => {
        if (e.type === "sent") showNotice(`Sent from the outbox: ${e.entry.subject || "(no subject)"}`);
        else if (e.type === "failed") showNotice(e.error, true);
        else loadFolders();
      }),
    [],
  );

  // A message opened: its notification, if one is still shown, has done its work.
  useEffect(() => {
    if (route.message) closeNotification(route.message);
  }, [route.message]);

  // The offline copy follows every folders answer (only folders whose change marker moved are compared).
  useEffect(() => {
    if (signedIn && online && settings && folders.length > 0) syncOffline(folders);
  }, [signedIn, online, settings, folders]);

  // Back online: send what waits in the outbox and was left waiting, check now, and show the server's listing instead
  // of the copy's.
  const wasOnline = useRef(online);
  useEffect(() => {
    if (online && !wasOnline.current && signedIn) {
      void flushOutbox();
      void resumeTurns(); // a folder marked read the app was closed before waits for the connection
      checkNow();
      reloadList();
    }
    wasOnline.current = online;
  }, [online]);

  // Another listing (a folder, a search, a page): nothing is chosen in it.
  const listingKey = route.view === "folder" ? `f:${route.folder}` : route.view === "search" ? `s:${JSON.stringify(route.search)}` : route.view;
  useEffect(() => setChecked(null), [listingKey]);
  useEffect(() => {
    if (!checked) setSelectingAll(false);
  }, [checked === null]);

  // A folder opened: check it against Yahoo (the server asks Yahoo at most every 15 s per folder).
  const firstFolder = useRef(true);
  useEffect(() => {
    if (firstFolder.current) {
      firstFolder.current = false;
      return;
    }
    if (signedIn && currentFolder && currentFolder !== "Inbox") checkNow();
  }, [currentFolder]);

  // A message being written is kept as a draft with an id in the URL; one from before there was one (an earlier
  // version's history, a restart) goes on with the draft kept for it, or starts one.
  useEffect(() => {
    if (signedIn && route.compose && !route.draft) {
      navigate({ ...route, draft: draftFor(draftTarget(route.compose, route.message))?.id ?? newKey() }, true);
    }
  }, [signedIn, route]);

  // The forwarding rules, each time they are opened (their counts move as mail is forwarded).
  const loadRules = useCallback(async () => {
    try {
      setRules((await api.forwardRules()).rules);
    } catch (e) {
      onError(e);
    }
  }, []);
  useEffect(() => {
    if (signedIn && inRules) loadRules();
  }, [signedIn, inRules]);

  useEffect(() => {
    const inbox = folders.find((f) => f.name === (currentFolder ?? "Inbox"));
    const label = route.compose
      ? COMPOSE_TITLES[route.compose]
      : route.view === "search"
        ? "Search"
        : route.view === "rules"
          ? "Forwarding rules"
          : route.view === "settings"
            ? "Offline"
            : route.view === "notifications"
              ? "Notifications"
              : route.view === "outbox"
                ? "Outbox"
                : route.view === "drafts"
                  ? "Drafts"
                  : route.view === "starred"
                    ? "Starred"
                    : (currentFolder ?? "Inbox");
    document.title = inbox?.unread ? `${label} (${inbox.unread}) · Mail` : `${label} · Mail`;
  }, [folders, route]);

  if (!signedIn) {
    return (
      <SignIn
        error={authError}
        onSignIn={() =>
          startSignIn(location.pathname + location.search).catch((e) => setAuthError(e instanceof Error ? e.message : String(e)))
        }
      />
    );
  }

  const go = (next: Route, replace = false) => {
    setDrawer(false);
    navigate(next, replace);
  };
  const listRoute: Route = { ...route, message: null, compose: undefined, draft: undefined };
  const shownRoute: Route = { ...route, compose: undefined, draft: undefined };
  const openMessage = (id: string) => go({ ...route, message: id, compose: undefined, draft: undefined }, route.message !== null);
  /** Writing a message on top of `base`: a reply or forward goes on with the draft kept for it, if there is one; a new
   * message is a draft of its own. */
  const composeRoute = (base: Route, mode: ComposeMode, source: string | null): Route => ({
    ...base,
    message: source,
    compose: mode,
    draft: (mode !== "new" && draftFor(draftTarget(mode, source))?.id) || newKey(),
  });
  const draftsRoute: Route = { view: "drafts", message: null };
  const folderNames = folders.map((f) => f.name);
  const searchParams: SearchParams =
    route.view === "search" ? route.search : inStarred ? { flagged: true, sort: "newest" } : { folder: currentFolder ?? "Inbox", sort: "newest" };
  const searching = route.view === "search";
  const shownFolder = folders.find((f) => f.name === currentFolder) ?? null;

  /** A change to a message shown in the lists from now; `settled` when the server has it (ok) or not (taken back). */
  const begun = (id: string, change: Change) => setOverrides((prev) => begin(prev, id, change));
  const settled = (id: string, change: Change, ok: boolean) => {
    const at = tick();
    setOverrides((prev) => settle(prev, id, change, ok, at));
  };

  /** Actions on messages, each done in the background in turn with the others (outbox.perform) and shown at once in
   * the lists until the server has it; when they are done (or not), the counts and listing follow. Tells `label` when
   * all are done or wait in the outbox, why not when one is not (unless `quiet` and only the connection is missing).
   * Resolves, for each, true when it is done or waits in the outbox, false when it is not done and was taken back. */
  const actAll = (items: Acted[], label: string | null, quiet = false): Promise<boolean[]> => {
    const done = items.map(({ action, subject, change }) => {
      if (change) begun(action.message, change);
      return perform(action, subject)
        .then(
          (p: Performed) => p,
          (error: unknown) => ({ error }),
        )
        .then((outcome) => {
          if (change) settled(action.message, change, !("error" in outcome));
          return outcome;
        });
    });
    return Promise.all(done).then((outcomes) => {
      const errors = outcomes.flatMap((o) => ("error" in o ? [o.error] : []));
      const performed = outcomes.flatMap((o) => ("error" in o ? [] : [o]));
      // skipped: the message was no longer where it was moved from here (another device was first); it is not
      // listed there either, so it stays gone from the list
      const skipped = performed.find((p) => p.skipped)?.skipped;
      if (errors.length > 0) {
        if (!(quiet && isNetworkError(errors[0]))) onError(errors[0]);
      } else if (skipped) showNotice(`Not done: ${skipped}`);
      else if (label) showNotice(performed.some((p) => p.queued) ? `${label} (waiting in the outbox)` : label);
      loadFolders();
      return outcomes.map((o) => !("error" in o));
    });
  };

  /** One action on a message, as actAll does it. */
  const act = (action: SingleAction, subject: string | null, change: Change | null, label: string | null, quiet = false): Promise<boolean> =>
    actAll([{ action, subject, change }], label, quiet).then(([ok]) => ok);

  /** An action on several messages at once: one action per folder they are taken from (`actions`, bulk.bulkActions),
   * each done in the background in turn with the others (outbox.perform) and shown in the lists at once (unless
   * `shown` already: a delete after its Undo). A notice (`busy`) stays while they are being done; when they are, one
   * says what was done (`label`, with the messages done and how many there were) and, for the messages the server
   * could not do, how many and why, with Retry (`retry`, with those rows). Those go back in the lists; those the
   * server found done already (no longer in the folder) stay gone, and the listing reloads. */
  const actMany = (actions: BulkAction[], rows: MessageRow[], change: Change, shown: boolean, busy: string, label: (done: string[], total: number) => string, retry: (rows: MessageRow[]) => void) => {
    if (!shown) for (const row of rows) begun(row.id, change);
    showBusy(busy);
    const runs = actions.map((a) => perform(a, null).then((p) => p, (error: unknown) => ({ error })));
    void Promise.all(runs).then((outcomes) => {
      const done: string[] = [];
      const failed: string[] = [];
      let reason: string | null = null;
      let queued = false;
      let signedOut: SignedOut | null = null;
      actions.forEach((a, i) => {
        const o = outcomes[i];
        if ("error" in o) {
          if (o.error instanceof SignedOut) signedOut = o.error;
          reason ??= errorText(o.error);
          for (const m of a.messages) {
            failed.push(m);
            settled(m, change, false);
          }
        } else if (o.queued) {
          queued = true;
          for (const m of a.messages) {
            done.push(m);
            settled(m, change, true);
          }
        } else {
          for (const r of o.outcomes ?? []) {
            const ok = r.status !== "failed";
            (ok ? done : failed).push(r.id);
            if (!ok) reason ??= r.reason ?? null;
            settled(r.id, change, ok);
          }
        }
      });
      if (signedOut) onError(signedOut);
      else {
        const text = resultText(label(done, rows.length) + (queued ? " (waiting in the outbox)" : ""), done.length, failed.length, reason);
        showResult(text, failed.length > 0, failed.length > 0 ? () => retry(rows.filter((r) => failed.includes(r.id))) : null);
      }
      loadFolders();
    });
  };

  /** A message open beside the list that leaves it is closed. */
  const closeLeaving = (rows: MessageRow[]) => {
    if (route.message && !route.compose && rows.some((r) => r.id === route.message)) back(listRoute);
  };

  /** A row swiped right: its star set or removed, as the star in the message's toolbar does. */
  const starRow = (row: MessageRow) => {
    const flagged = !row.flagged;
    void act({ kind: "flag", message: row.id, flagged }, row.subject, { flagged }, flagged ? "Starred" : "Star removed");
  };

  /** The folder an action on a listed message takes it from: the folder listed (a search or the starred mail: the
   * first folder it is in outside Trash, as the message's own toolbar does). */
  const fromOf = (row: MessageRow) => actionFolder(row.folders.map((folder) => ({ folder })), currentFolder) ?? "";

  /** A trash from Trash itself (or Deleted) deletes for good. */
  const deletedFrom = (from: string) => ["trash", "deleted"].includes(from.toLowerCase());

  /** A row swiped left, or the rows chosen deleted: gone from the list at once, with Undo in the notice; sent to Trash
   * (deleted for good from Trash itself) once the notice goes without it, from the folder listed: one message as its
   * own action, several as one action per folder (trashMany). A message open beside the list is closed. */
  const trashRows = (rows: MessageRow[]) => {
    const from = rows.map(fromOf);
    const change: Change = { removed: true };
    endUndo(true);
    for (const row of rows) begun(row.id, change);
    undoable.current = {
      commit: () => {
        if (rows.length > 1) return trashMany(rows, true);
        // shown already (begun): settled once done
        const items = rows.map((row, i): Acted => ({ action: { kind: "trash", message: row.id, from: from[i] }, subject: row.subject, change: null }));
        actAll(items, null).then((oks) => rows.forEach((row, i) => settled(row.id, change, oks[i])));
      },
      undo: () => rows.forEach((row) => settled(row.id, change, false)),
    };
    const deleted = from.filter(deletedFrom).length;
    setNotice({ text: trashedText(rows.length - deleted, deleted), error: false, undo: true });
    closeLeaving(rows);
  };

  /** Several rows to Trash (deleted for good from Trash itself) as one action per folder they are listed in, after
   * their Undo has had its time (`shown`: the rows are gone from the list already) or again from the notice's Retry. */
  const trashMany = (rows: MessageRow[], shown: boolean) => {
    const fromById = new Map(rows.map((r) => [r.id, fromOf(r)]));
    const actions = bulkActions(rows, fromOf, (messages, from) => ({ kind: "trash-many", messages, from }));
    const deleting = rows.every((r) => deletedFrom(fromById.get(r.id) ?? ""));
    const label = (done: string[], total: number) => {
      const deleted = done.filter((id) => deletedFrom(fromById.get(id) ?? "")).length;
      return trashedText(done.length - deleted, deleted, total);
    };
    actMany(actions, rows, { removed: true }, shown, busyText(actions[0], rows.length, deleting), label, (again) => trashMany(again, false));
  };

  // Several messages chosen: those of the rows loaded that are in Yahoo. Select all ticks them all, loading the rest
  // of the listing first (the list has it load on while selectingAll, up to SELECT_ALL_MAX rows). An action on them
  // is done on all of them as one, leaving out those it would not change, and the list goes back to opening messages.
  const choosable = listed.filter((r) => r.folders.length > 0);
  const chosen = checked ? choosable.filter((r) => checked.has(r.id)) : [];
  const allLoaded = listing.complete || listed.length >= SELECT_ALL_MAX;
  const allChosen = choosable.length > 0 && chosen.length === choosable.length && allLoaded;
  useEffect(() => {
    if (!selectingAll || !checked) return;
    setChecked(new Set(choosable.map((r) => r.id)));
    if (allLoaded) setSelectingAll(false);
  }, [selectingAll, listed, allLoaded]);
  const selectAll = () => {
    if (allChosen) {
      setChecked(new Set());
      return;
    }
    setChecked(new Set(choosable.map((r) => r.id)));
    if (!allLoaded) setSelectingAll(true);
  };
  const toggleChecked = (id: string) =>
    setChecked((prev) => {
      if (!prev) return prev;
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  // One message chosen is acted on as from its own toolbar; several as one action (per folder they are taken from),
  // which the notice's Retry does again on the messages not done (starMany, readMany, moveMany).
  const starMany = (rows: MessageRow[], flagged: boolean) => {
    const actions = bulkActions(rows, null, (messages) => ({ kind: "flag-many", messages, flagged }));
    actMany(actions, rows, { flagged }, false, busyText(actions[0], rows.length), (done, total) => starredText(done.length, flagged, total), (again) => starMany(again, flagged));
  };
  const starChosen = (flagged: boolean) => {
    const rows = chosen.filter((r) => r.flagged !== flagged);
    if (rows.length > 1) starMany(rows, flagged);
    else void actAll(rows.map((r) => ({ action: { kind: "flag", message: r.id, flagged }, subject: r.subject, change: { flagged } })), starredText(rows.length, flagged));
    setChecked(null);
  };
  const readMany = (rows: MessageRow[], read: boolean) => {
    const actions = bulkActions(rows, null, (messages) => ({ kind: "read-many", messages, read }));
    actMany(actions, rows, { unread: !read }, false, busyText(actions[0], rows.length), (done, total) => readText(done.length, read, total), (again) => readMany(again, read));
  };
  const readChosen = (read: boolean) => {
    const rows = chosen.filter((r) => r.unread === read);
    if (rows.length > 1) readMany(rows, read);
    else void actAll(rows.map((r) => ({ action: { kind: "read", message: r.id, read }, subject: r.subject, change: { unread: !read } })), readText(rows.length, read));
    setChecked(null);
  };
  const moveMany = (rows: MessageRow[], folder: string) => {
    const actions = bulkActions(rows, fromOf, (messages, from) => ({ kind: "move-many", messages, from, folder }));
    actMany(actions, rows, { removed: true }, false, busyText(actions[0], rows.length), (done, total) => movedText(done.length, folder, total), (again) => moveMany(again, folder));
  };
  const moveChosen = (folder: string) => {
    const rows = chosen.filter((r) => !r.folders.includes(folder));
    if (rows.length > 1) moveMany(rows, folder);
    else {
      void actAll(
        rows.map((r) => ({ action: { kind: "move", message: r.id, from: fromOf(r), folder }, subject: r.subject, change: { removed: true } })),
        movedText(rows.length, folder),
      );
    }
    closeLeaving(rows);
    setChecked(null);
  };
  const trashChosen = () => {
    trashRows(chosen);
    setChecked(null);
  };

  /** Every message in the folder marked read, after asking: at once in the list and the folder's count, done in the
   * background in turn with the actions on messages, and only up to the mail the count asked about (`upto`), so mail
   * that comes before it is done stays unread. Needs the connection (it is not one for the outbox). */
  const markAllRead = (folder: Folder) => {
    const n = folder.unread;
    if (!confirm(`Mark ${n === 1 ? "the unread message" : `all ${n.toLocaleString()} unread messages`} in ${folder.name} as read?`)) return;
    const key = folder.name.toLowerCase();
    const change: Change = { unread: false };
    setFolderReads((prev) => begin(prev, key, change));
    setFolders((prev) => prev.map((f) => (f.name === folder.name ? { ...f, unread: 0 } : f)));
    markFolderRead(folder.name, folder.upto)
      .then(
        ({ marked }) => {
          showNotice(marked === 0 ? `Nothing was unread in ${folder.name}` : `Marked ${marked.toLocaleString()} ${marked === 1 ? "message" : "messages"} in ${folder.name} as read`);
          return true;
        },
        (e: unknown) => {
          onError(e);
          return false;
        },
      )
      .then((ok) => {
        const at = tick();
        setFolderReads((prev) => settle(prev, key, change, ok, at));
        loadFolders();
      });
  };

  const onSent = (result: SendResult) => {
    showNotice(result.already_sent ? "Sent (the earlier attempt went through)" : "Sent");
    loadFolders();
    back(shownRoute);
  };
  const onQueued = () => {
    showNotice("In the outbox: it is sent when the app can reach the server");
    back(shownRoute);
  };
  const onComposeClosed = (kept: boolean) => {
    if (kept) showNotice("Saved in Drafts on this device");
    back(shownRoute);
  };

  const rulesRoute: Route = { view: "rules", message: null };
  const onRuleSaved = (rule: ForwardRule, created: boolean) => {
    setRules((prev) => (prev?.some((r) => r.id === rule.id) ? prev.map((r) => (r.id === rule.id ? rule : r)) : [...(prev ?? []), rule]));
    showNotice(created ? "Rule created" : "Rule saved");
    if (created) go({ ...rulesRoute, rule: String(rule.id) }, true);
    loadRules();
  };
  const onRuleDeleted = () => {
    showNotice("Rule deleted");
    loadRules();
    back(rulesRoute);
  };

  const doSignOut = open
    ? null
    : async () => {
        const n = outbox.entries.length;
        if (n > 0 && !confirm(`${n === 1 ? "One message or action has" : `${n} messages and actions have`} not been sent yet and will be deleted with the mail on this device. Sign out?`)) return;
        await signOut();
        await forgetOfflineCopy();
        forgetOutbox();
        setSignedIn(false);
        setAuthError(null);
      };
  const offlineNames = new Set((settings?.offline_folders ?? []).map((f) => f.folder.toLowerCase()));

  return (
    <div class={`app ${route.message || route.compose || route.rule ? "has-message" : ""} ${drawer ? "drawer-open" : ""} ${checked ? "choosing" : ""}`}>
      <aside class="sidebar">
        <FolderList
          folders={folders}
          current={currentFolder}
          lastSync={lastSync}
          me={me}
          rulesOpen={inRules}
          settingsOpen={inSettings}
          notificationsOpen={inNotifications}
          outboxOpen={inOutbox}
          draftsOpen={inDrafts}
          starredOpen={inStarred}
          waiting={outbox.entries.length}
          offline={offlineNames}
          online={online}
          onSelect={(f) => go({ view: "folder", folder: f, message: null })}
          onStarred={() => go({ view: "starred", message: null })}
          onRules={() => go(rulesRoute)}
          onSettings={() => go({ view: "settings", message: null })}
          onNotifications={() => go({ view: "notifications", message: null })}
          onOutbox={() => go({ view: "outbox", message: null })}
          onDrafts={() => go(draftsRoute)}
          onSignOut={doSignOut}
        />
      </aside>
      {drawer && <div class="scrim" onClick={() => setDrawer(false)} />}

      <section
        class="pane list-pane"
        aria-label={inRules ? "Forwarding rules" : inSettings ? "Offline" : inNotifications ? "Notifications" : inOutbox ? "Outbox" : inDrafts ? "Drafts" : "Messages"}
      >
        {inDrafts ? (
          <Drafts
            selected={route.compose ? (route.draft ?? null) : null}
            onMenu={() => setDrawer(true)}
            onOpen={({ mode, source, draft }) => go({ ...draftsRoute, message: source, compose: mode, draft }, route.compose !== undefined)}
            onNew={() => go(composeRoute(draftsRoute, "new", null), route.compose !== undefined)}
          />
        ) : inOutbox ? (
          <Outbox
            online={online}
            onMenu={() => setDrawer(true)}
            onEdit={({ mode, source, draft }) => go({ ...draftsRoute, message: source, compose: mode, draft })}
            onError={onError}
          />
        ) : inNotifications ? (
          <NotificationSettings
            folders={folders}
            settings={settings}
            online={online}
            onSaved={setSettings}
            onMenu={() => setDrawer(true)}
            onError={onError}
            onNotice={(t) => showNotice(t)}
          />
        ) : inSettings ? (
          <OfflineSettings folders={folders} settings={settings} online={online} onSaved={setSettings} onMenu={() => setDrawer(true)} onError={onError} />
        ) : inRules ? (
          <RuleList rules={rules} selected={route.rule} onOpen={(id) => go({ ...rulesRoute, rule: id }, route.rule !== undefined)} onMenu={() => setDrawer(true)} />
        ) : checked ? (
          <SelectBar
            count={chosen.length}
            total={listing.total}
            complete={allLoaded}
            loading={selectingAll}
            all={allChosen}
            canAll={choosable.length > 0 || !allLoaded}
            onAll={selectAll}
            onCancel={() => setChecked(null)}
          />
        ) : searching ? (
          <SearchForm
            initial={route.search}
            folders={folderNames}
            autoFocus={Object.keys(route.search).length === 0}
            onSearch={(s) => go({ view: "search", search: s, message: null }, true)}
            onClose={() => go({ view: "folder", folder: route.search.folder ?? "Inbox", message: null })}
            onSelect={Object.keys(route.search).length > 0 ? () => setChecked(new Set()) : undefined}
          />
        ) : (
          <div class="toolbar">
            <button class="icon-button menu" title="Folders" onClick={() => setDrawer(true)}>
              <Icon name="menu" />
            </button>
            <h2 class="title">{inStarred ? "Starred" : currentFolder}</h2>
            <span class="spacer" />
            <button class="icon-button" title="Select messages" onClick={() => setChecked(new Set())}>
              <Icon name="select" />
            </button>
            {shownFolder && shownFolder.unread > 0 && (
              <button
                class="icon-button"
                title={online ? "Mark all as read" : "Mark all as read (needs a connection)"}
                disabled={!online}
                onClick={() => markAllRead(shownFolder)}
              >
                <Icon name="readAll" />
              </button>
            )}
            <button class="icon-button" title="Check for new mail" onClick={checkNow}>
              <Icon name="refresh" />
            </button>
            <button class="icon-button" title="Search" onClick={() => go({ view: "search", search: {}, message: null })}>
              <Icon name="search" />
            </button>
            <button class="icon-button" title="New message" onClick={() => go(composeRoute(route, "new", null))}>
              <Icon name="compose" />
            </button>
          </div>
        )}
        {!online && !inPage && (
          <div class="banner offline-banner" role="status">
            <span>
              Offline: showing the mail kept on this device.
              {outbox.entries.length > 0 && ` ${outbox.entries.length.toLocaleString()} waiting in the outbox.`}
            </span>
          </div>
        )}
        {inPage ? null : searching && Object.keys(route.search).length === 0 ? (
          <p class="empty">
            Words must all occur (Danish and English word forms; Exact match in the filters takes them as written). Use OR between alternatives, "quotes" for a
            phrase and -word to exclude.
          </p>
        ) : (
          <MessageList
            params={searchParams}
            version={version}
            overrides={overrides}
            folderReads={folderReads}
            waiting={outbox.waiting}
            selected={route.message}
            checked={checked}
            emptyText={searching ? "Nothing found." : inStarred ? "No starred messages: the star in a message's toolbar puts it here." : "No messages in this folder."}
            showFolders={searching || inStarred}
            noun={inStarred ? "message" : "result"}
            onOpen={openMessage}
            onCheck={toggleChecked}
            onListed={(rows, info) => {
              setListed(rows);
              setListing(info);
            }}
            loadAll={selectingAll}
            onStar={starRow}
            onTrash={(row) => trashRows([row])}
            onRefresh={async () => {
              if (searching || inStarred) reloadList();
              if (!searching) await checkNow();
            }}
            onError={onError}
          />
        )}
        {checked && !inPage && <BulkBar rows={chosen} folders={folderNames} onTrash={trashChosen} onMove={moveChosen} onStar={starChosen} onRead={readChosen} />}
      </section>

      <section class="pane message-pane" aria-label={inRules ? "Forwarding rule" : "Message"}>
        {inOutbox ? (
          <div class="placeholder">
            <Icon name="sent" size={48} />
          </div>
        ) : inNotifications ? (
          <div class="placeholder">
            <Icon name="bell" size={48} />
          </div>
        ) : inSettings ? (
          <div class="placeholder">
            <Icon name="download" size={48} />
          </div>
        ) : inRules ? (
          route.rule ? (
            <RulePane
              id={route.rule}
              rules={rules}
              onBack={() => back(rulesRoute)}
              onSaved={onRuleSaved}
              onDeleted={onRuleDeleted}
              onOpenMessage={(id, folder) => go({ view: "folder", folder: folder ?? "Inbox", message: id })}
              onError={onError}
            />
          ) : (
            <div class="placeholder">
              <Icon name="forward" size={48} />
            </div>
          )
        ) : route.compose ? (
          me && route.draft ? (
            <Compose
              key={`${route.compose}:${route.message ?? ""}:${route.draft}`}
              mode={route.compose}
              source={route.message}
              draftId={route.draft}
              me={me.yahoo_user}
              onClose={onComposeClosed}
              onSent={onSent}
              onQueued={onQueued}
              onError={onError}
            />
          ) : (
            <div class="sentinel">
              <span class="spinner" aria-label="Loading" />
            </div>
          )
        ) : inDrafts ? (
          <div class="placeholder">
            <Icon name="compose" size={48} />
          </div>
        ) : route.message ? (
          <MessageView
            id={route.message}
            folders={folderNames}
            folder={currentFolder}
            me={me?.yahoo_user ?? null}
            onBack={() => back(listRoute)}
            onOpen={(id) => go({ ...route, message: id, compose: undefined, draft: undefined })}
            onSearch={(s) => go({ view: "search", search: s, message: null })}
            onCompose={(mode) => go(composeRoute(route, mode, route.message))}
            onAction={act}
            onError={onError}
            onNotice={(t) => showNotice(t)}
          />
        ) : (
          <div class="placeholder">
            <Icon name="unread" size={48} />
          </div>
        )}
      </section>

      {notice && (
        <div
          class={`notice ${notice.error ? "error" : ""} ${notice.undo || notice.action ? "undoable" : ""}`}
          role="status"
          onClick={() => {
            if (notice.sticky) return; // it goes when the action is done
            if (notice.undo) endUndo(true);
            dropNotice(notice);
          }}
        >
          {notice.text}
          {(notice.undo || notice.action) && (
            <button
              class="notice-action"
              onClick={(e) => {
                e.stopPropagation();
                if (notice.undo) endUndo(false);
                else notice.action?.run();
                dropNotice(notice);
              }}
            >
              {notice.undo ? "Undo" : notice.action?.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function SignIn({ error, onSignIn }: { error: string | null; onSignIn: () => void }) {
  const [busy, setBusy] = useState(false);
  return (
    <main class="signin">
      <img src="/favicon.svg" alt="" width={72} height={72} />
      <h1>Yahoo Mail</h1>
      <p class="muted">Sign in with your Microsoft work account to read your mail.</p>
      {error && <p class="warn-text">{error}</p>}
      <button
        class="primary"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          onSignIn();
          setTimeout(() => setBusy(false), 10_000);
        }}
      >
        Sign in
      </button>
    </main>
  );
}
